import { useEffect } from 'react';
import { Text } from '@belong/ui';
import type {
  ConsumerFailedPaymentModel,
  ConsumerPaymentMethodSummaryModel,
  ConsumerWireInstructionsModel,
} from '../failed-payments.types';
import type { PaymentAccountResourceModel } from 'api/models';
import { format } from 'date-fns';
import { PaymentPreference } from 'models/enums';
import { FAILED_PAYMENTS_STRINGS } from '../failed-payments-strings';
import { mapPaymentAccountToPaymentMethodSummary } from '../failed-payments-utils';
import { FailedPaymentMethodSelector } from '../failed-payment-method-selector';
import { WireDetails } from './homeowner-change-payment-method-subsection';

type RetryTodaySubsectionProps = {
  failedPayments: ConsumerFailedPaymentModel[];
  paymentMethods: PaymentAccountResourceModel[];
  paymentAccountUniqueId: string | null;
  paymentMethod: ConsumerPaymentMethodSummaryModel | null | undefined;
  onSelectPaymentMethod: (id: string) => void;
  canPayByWire?: boolean;
  payByWireSelected?: boolean;
  onSelectPayByWire?: () => void;
  wireInstructions?: ConsumerWireInstructionsModel | null;
  wireInstructionsLoading?: boolean;
  wireCoveringBalanceUniqueIds?: string[];
  onToggleWireCovering?: (balanceUniqueIds: string[]) => void;
  wireReceiptFile?: File | null;
  onWireReceiptChange?: (file: File | null) => void;
};

export function RetryTodaySubsection({
  failedPayments,
  paymentMethods,
  paymentAccountUniqueId,
  paymentMethod,
  onSelectPaymentMethod,
  canPayByWire = false,
  payByWireSelected = false,
  onSelectPayByWire,
  wireInstructions = null,
  wireInstructionsLoading = false,
  wireCoveringBalanceUniqueIds = [],
  onToggleWireCovering,
  wireReceiptFile = null,
  onWireReceiptChange,
}: RetryTodaySubsectionProps) {
  const availablePaymentMethods = paymentMethods.filter(
    (method) => method.accountId && method.isVerified,
  );
  const selectedPaymentMethod =
    availablePaymentMethods.find(
      (method) => method.accountId === paymentAccountUniqueId,
    ) ?? availablePaymentMethods[0];
  const selectedPaymentMethodSummary = selectedPaymentMethod
    ? mapPaymentAccountToPaymentMethodSummary(selectedPaymentMethod)
    : paymentMethod;
  const retryDateLabel = format(new Date(), 'MMM d, yyyy');

  useEffect(() => {
    if (payByWireSelected) return;
    if (!paymentAccountUniqueId && selectedPaymentMethod?.accountId) {
      onSelectPaymentMethod(selectedPaymentMethod.accountId);
    }
  }, [
    onSelectPaymentMethod,
    paymentAccountUniqueId,
    payByWireSelected,
    selectedPaymentMethod?.accountId,
  ]);

  const hasSelectableMethod = Boolean(selectedPaymentMethodSummary);

  return (
    <div className="flex flex-col gap-2xl">
      <div className="flex flex-col gap-sm">
        <div className="flex flex-col gap-xs">
          <Text variant="h3" fontWeight="semibold">
            {payByWireSelected
              ? FAILED_PAYMENTS_STRINGS['retry_today.wire_prompt']
              : hasSelectableMethod
                ? FAILED_PAYMENTS_STRINGS['retry_today.prompt']
                : FAILED_PAYMENTS_STRINGS['retry_today.no_method_prompt']}
          </Text>
          <Text variant="body">
            {payByWireSelected
              ? FAILED_PAYMENTS_STRINGS['retry_today.wire_description']
              : hasSelectableMethod
                ? `${FAILED_PAYMENTS_STRINGS['retry_today.rescheduled_for']} ${retryDateLabel}`
                : FAILED_PAYMENTS_STRINGS['retry_today.no_method_description']}
          </Text>
        </div>

        <FailedPaymentMethodSelector
          paymentMethod={selectedPaymentMethodSummary}
          selectedPaymentAccountUniqueId={paymentAccountUniqueId}
          paymentType={PaymentPreference.Rent}
          onSelectPaymentMethod={onSelectPaymentMethod}
          wireTransferSelected={payByWireSelected}
          onSelectWireTransfer={canPayByWire ? onSelectPayByWire : undefined}
        />
      </div>

      {payByWireSelected && onToggleWireCovering && onWireReceiptChange && (
        <WireDetails
          failedPayments={failedPayments}
          wireInstructions={wireInstructions}
          wireInstructionsLoading={wireInstructionsLoading}
          wireCoveringBalanceUniqueIds={wireCoveringBalanceUniqueIds}
          onToggleWireCovering={onToggleWireCovering}
          wireReceiptFile={wireReceiptFile}
          onWireReceiptChange={onWireReceiptChange}
        />
      )}
    </div>
  );
}
